import type { Coordinate } from '../general';

import type { Polygon } from './getHotels';
import type { FerryRouteStaticInfo } from './transport';

interface BaseSuggestionCriteria {
  searchType: AutoCompleteSearchType;
}

interface TextSuggestionCriteria extends BaseSuggestionCriteria {
  searchText: string;
}

interface PositionSuggestionCriteria extends BaseSuggestionCriteria {
  position: Coordinate;
  searchType: AutoCompleteSearchType.Flight;
}

export type SearchSuggestionCriteria =
  TextSuggestionCriteria | PositionSuggestionCriteria;

export function isPositionSuggestionCriteria(
  criteria: SearchSuggestionCriteria
): criteria is PositionSuggestionCriteria {
  return 'position' in criteria;
}

export interface AutoCompleteSuggestionsByCategory {
  label: string;
  type: CategoryType;
  data: AutoCompleteSuggestion[];
}

export const enum CategoryType {
  Destination = 'Destination',
  Region = 'Region',
  Theme = 'Theme',
  Hotel = 'Hotel',
  Airport = 'Airport',
  City = 'City',
  PolygonCityZone = 'PolygonCityZone',
  HotelInformation = 'HotelInformation',
  Location = 'Location',
  FerryRoute = 'FerryRoute',
  GeolocationPosition = 'GeolocationPosition',
}

export const enum AutoCompleteSearchType {
  MapLocation = 'mapLocation',
  Destination = 'destination',
  Flight = 'flight',
  FerryRoute = 'ferryRoute',
}

const destinationCategoryTypes = [
  CategoryType.Destination,
  CategoryType.Region,
  CategoryType.Theme,
  CategoryType.Airport,
  CategoryType.City,
  CategoryType.PolygonCityZone,
  CategoryType.HotelInformation,
] as const;

export type DestinationCategoryType = (typeof destinationCategoryTypes)[number];

export type FlightCategoryType = CategoryType.Airport | CategoryType.City;

interface BaseAutoCompleteSuggestion<T> {
  label: string;
  category: string;
  categoryType: T;
}

export interface DestinationAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<DestinationCategoryType> {
  popularityScore: number;
  tooltip: string;
  polygonId: number;
}

export interface HotelAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<CategoryType.Hotel> {
  popularityScore: number;
  tooltip: string;
  polygons?: Polygon[];
  hotel: {
    hotelCode: string;
    mainPolygonFullName: string;
    mainPolygonId: number;
  };
}

export interface FlightAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<FlightCategoryType> {
  iata: string;
  tooltip?: string;
}

export interface LocationAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<CategoryType.Location> {
  placeId: string;
}

export interface FerryAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<CategoryType.FerryRoute> {
  data: FerryRouteStaticInfo;
}

export interface GeolocationPositionAutoCompleteSuggestion extends BaseAutoCompleteSuggestion<CategoryType.GeolocationPosition> {
  isCurrentPosition?: boolean;
}

export type AutoCompleteSuggestion =
  | DestinationAutoCompleteSuggestion
  | HotelAutoCompleteSuggestion
  | FlightAutoCompleteSuggestion
  | LocationAutoCompleteSuggestion
  | FerryAutoCompleteSuggestion
  | GeolocationPositionAutoCompleteSuggestion;

export function isGeolocationPositionAutoCompleteSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is GeolocationPositionAutoCompleteSuggestion {
  return suggestion.categoryType === CategoryType.GeolocationPosition;
}

export function isDestinationAutoCompleteSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is DestinationAutoCompleteSuggestion {
  return destinationCategoryTypes.includes(
    suggestion.categoryType as DestinationCategoryType
  );
}

export function isFerryRouteSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is FerryAutoCompleteSuggestion {
  return suggestion.categoryType === CategoryType.FerryRoute;
}

export function isAirportSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is FlightAutoCompleteSuggestion {
  return suggestion.categoryType === CategoryType.Airport;
}

export function isLocationSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is LocationAutoCompleteSuggestion {
  return suggestion.categoryType === CategoryType.Location;
}

export function isHotelAutoCompleteSuggestion(
  suggestion: AutoCompleteSuggestion
): suggestion is HotelAutoCompleteSuggestion {
  return suggestion.categoryType === CategoryType.Hotel;
}
